import {
  type ChangeEvent,
  type Dispatch,
  type MouseEvent,
  type ReactElement,
  type SetStateAction,
  useState,
} from "react"

import { useTranslation } from "react-i18next"

import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined"
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined"
import { Grid } from "@mui/material"
import { Button, Pagination } from "@synapse-analytics/synapse-ui"
import { isEmpty as _isEmpty } from "lodash"

import { GroupSelectMenu, type MenuOption } from "../../../../components/UI/GroupedSelectMenu/GroupSelectMenu"
import { BaseBarChart } from "../../../../components/graphs/Bar/BaseBarChart"
import { FeedbackInsightList } from "../../../../types/generated/api/FeedbackInsightList"
import { InsightList } from "../../../../types/generated/api/InsightList"

type ChartContainerProps = {
  setIsChartFullWidth: Dispatch<SetStateAction<boolean>>
  isChartFullWidth: boolean
  isLoading: boolean
  LabelReasonFeedbackInsightData: Record<string, Record<string, Record<string, number>>> | undefined
  insight: InsightList | FeedbackInsightList
  initialSelectedLabel?: string
  initialSelectedReasons?: MenuOption[]
  initialSelectedFeedbacks?: { reason: string; feedback: MenuOption }[]
}

/**
 * ChartContainer component
 *
 * A container component that displays a bar chart of
 * insights from the LabelReasonFeedbackInsight API.
 *
 * It fetches and processes label-reasons insight data to show
 * graphical representations of feedback distribution across
 * different labels and their associated reasons.
 *
 * Features:
 * - Responsive layout that shows either one or two graphs based on screen size
 * - Processes raw feedback data into a format suitable for visualization
 * - Handles loading and empty states
 *
 * @prop {boolean} isChartFullWidth - flag to render chart in full screen width
 * @prop {boolean} isLoading - flag to indicate if the data is still loading
 * @prop {Record<string, Record<string, Record<string, number>>>} LabelReasonFeedbackInsightData - data from the API
 * @prop {InsightList | FeedbackInsightList} insight - Insight metadata
 * @prop {string} initialSelectedLabel - initial label to be selected
 * @prop {MenuOption[]} initialSelectedReasons - initial reasons to be selected
 * @prop {{ reason: string; feedback: MenuOption }[]} initialSelectedFeedbacks - initial feedbacks to be selected
 */
export function ChartContainer(props: Readonly<Readonly<ChartContainerProps>>): ReactElement {
  const {
    isChartFullWidth,
    setIsChartFullWidth,
    isLoading,
    LabelReasonFeedbackInsightData,
    insight,
    initialSelectedLabel,
    initialSelectedReasons,
    initialSelectedFeedbacks,
  } = props

  const { t } = useTranslation("insights")

  const [selectedLabel, setSelectedLabel] = useState<string>(initialSelectedLabel ?? "")

  const [selectedReasons, setSelectedReasons] = useState<MenuOption[]>(initialSelectedReasons ?? [])

  const [feedbackSelectMenuOptions, setFeedbackSelectMenuOptions] = useState<
    { reason: string; feedback: MenuOption }[]
  >(initialSelectedFeedbacks ?? [])

  const [selectedFeedbacksItems, setSelectedFeedbacksItems] = useState<{ reason: string; feedback: MenuOption }[]>(
    initialSelectedFeedbacks ?? [],
  )

  const [page, setPage] = useState<number>(0) // State for current page number
  const [pageSize, setPageSize] = useState<number>(3) // State for number of items per page

  const adjustedGraphData = Object.entries(Object.groupBy(selectedFeedbacksItems, ({ reason }) => reason)).map(
    ([reason, items]) => {
      const feedbacks: Record<string, number | string> = {}

      items?.forEach((item) => {
        feedbacks[item.feedback.label] = item.feedback.value
      })

      return {
        reason,
        ...feedbacks,
      }
    },
  )

  /**
   * Handles checkbox change events on the GroupSelectMenu component.
   *
   * When a checkbox is checked, it adds the corresponding feedbacks to the
   * state and updates the selected reasons and feedbacks.
   *
   * When a checkbox is unchecked, it removes the corresponding feedbacks from
   * the state and updates the selected reasons and feedbacks.
   *
   * @param {ChangeEvent<HTMLInputElement>} ev - The checkbox change event.
   * @param {MenuOption} { id, label } - The checkbox element.
   */
  const onLabelReasonCheckboxChangeHandler = (
    _: unknown,
    { id, label }: MenuOption,
    ev: ChangeEvent<HTMLInputElement>,
  ): void => {
    if (ev.target.checked) {
      const feedbacks = Object.entries(LabelReasonFeedbackInsightData?.[selectedLabel]?.[label] ?? {}).map(
        ([feedback, value]) => ({
          reason: label,
          feedback: {
            id: `${selectedLabel}-${label}-${feedback}`,
            label: feedback,
            value: value,
          },
        }),
      )

      setSelectedReasons((prev) => [...prev, { id, label, value: label }])
      setFeedbackSelectMenuOptions((prev) => [...prev, ...feedbacks])
      setSelectedFeedbacksItems((prev) => [...prev, ...feedbacks])
    } else {
      setSelectedReasons((prev) => prev.filter((reason) => reason.id !== id))
      setFeedbackSelectMenuOptions((prev) => prev.filter((item) => !item.feedback.id.includes(label)))
      setSelectedFeedbacksItems((prev) => prev.filter((item) => !item.feedback.id.includes(label)))
    }
  }

  // formatted groups for the GroupSelectMenu
  const groupedSelectedLabelsOptions = LabelReasonFeedbackInsightData
    ? Object.entries(LabelReasonFeedbackInsightData).map(([key, value]) => ({
        id: key,
        label: key,
        value: key,
        options: Object.keys(value).map((option) => ({ id: `${key}-${option}`, label: option, value: option })),
      }))
    : []

  const groupedSelectedFeedbackOptions = Object.entries(
    Object.groupBy(feedbackSelectMenuOptions, ({ reason }) => reason),
  ).map(([key, value]) => ({
    id: key,
    value: key,
    label: key,
    options: value?.map((item) => item.feedback) ?? [],
  }))

  /**
   * Handles a checkbox change event for a feedback item in the GroupSelectMenu.
   * If the checkbox is checked, adds the feedback item to the selectedFeedbacksItems
   * state array. If the checkbox is unchecked, removes the feedback item from the
   * selectedFeedbacksItems state array.
   * @param {MenuOption} group - The group item that the feedback item belongs to
   * @param {MenuOption} item - The feedback item that was checked/unchecked
   * @param {ChangeEvent<HTMLInputElement>} ev - The change event
   */
  const onFeedbackCheckboxChangeHandler = (
    group: MenuOption,
    { id, label, value }: MenuOption,
    ev: ChangeEvent<HTMLInputElement>,
  ): void => {
    if (ev.target.checked) {
      setSelectedFeedbacksItems((prev) => [...prev, { reason: group.label, feedback: { id, label, value: value } }])
    } else {
      setSelectedFeedbacksItems((prev) => prev.filter((item) => item.feedback.id !== id))
    }
  }

  const initialFeedbacks = initialSelectedFeedbacks?.map((item) => item.feedback)

  const graphUniqueKeys = Array.from(new Set(selectedFeedbacksItems.map((item) => item.feedback.label)))
  const isEmpty = !LabelReasonFeedbackInsightData || _isEmpty(LabelReasonFeedbackInsightData)

  const customChartHeader = (
    <Grid maxWidth={"fit-content"} ml="auto">
      <Button
        type="button"
        size="small"
        variant="ghost-secondary"
        fullWidth={false}
        onClick={() => setIsChartFullWidth((prev) => !prev)}
      >
        {isChartFullWidth ? (
          <CloseFullscreenOutlinedIcon style={{ width: "20px", height: "20px" }} />
        ) : (
          <OpenInFullOutlinedIcon style={{ width: "20px", height: "20px" }} />
        )}
      </Button>
    </Grid>
  )

  const subHeader = (): React.ReactElement => {
    return (
      <Grid container columnSpacing={1} xs={12} flexWrap={"nowrap"}>
        <Grid item>
          <GroupSelectMenu
            title={t("LabelReasonFeedback.labels.labelReason")}
            menuTitle={t("LabelReasonFeedback.labels.labels")}
            groups={groupedSelectedLabelsOptions}
            initialInputValue={initialSelectedLabel}
            enableClearBtn
            enableMultiGroupSelect={false}
            initialSelectedOptions={initialSelectedReasons}
            onRadioBtnSelect={(ev: MouseEvent<HTMLInputElement>): void => {
              setSelectedLabel(ev.currentTarget.value)
              setSelectedReasons([])
              setSelectedFeedbacksItems([])
              setFeedbackSelectMenuOptions([])
            }}
            onCheckboxChange={onLabelReasonCheckboxChangeHandler}
            onClearAll={() => {
              setSelectedReasons([])
              setSelectedFeedbacksItems([])
              setFeedbackSelectMenuOptions([])
            }}
            isLoading={isLoading}
            inputMenuProps={{ menuMaxContent: true }}
          />
        </Grid>

        <Grid item>
          <GroupSelectMenu
            key={selectedReasons.length}
            title={t("LabelReasonFeedback.labels.feedback")}
            groups={groupedSelectedFeedbackOptions}
            inputValue={
              selectedFeedbacksItems === undefined || selectedFeedbacksItems.length === 0
                ? t("LabelReasonFeedback.feedback.all")
                : selectedFeedbacksItems?.length > 0
                  ? t("LabelReasonFeedback.feedback.selected", { count: selectedFeedbacksItems?.length })
                  : t("LabelReasonFeedback.feedback.select")
            }
            initialInputValue={
              initialFeedbacks === undefined || initialFeedbacks.length === 0
                ? t("LabelReasonFeedback.feedback.all")
                : initialFeedbacks?.length > 0
                  ? t("LabelReasonFeedback.feedback.selected", { count: initialFeedbacks?.length })
                  : t("LabelReasonFeedback.feedback.select")
            }
            enableMultiGroupSelect={true}
            enableClearBtn={false}
            initialSelectedOptions={initialFeedbacks}
            onCheckboxChange={onFeedbackCheckboxChangeHandler}
            inputMenuProps={{ menuMaxContent: true }}
          />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid item pl={2} max-width={"100%"} xs={isChartFullWidth ? 12 : 6}>
      <BaseBarChart
        title={t("LabelReasonFeedback.title")}
        customHeaderComponent={customChartHeader}
        data={adjustedGraphData.slice(page * pageSize, (page + 1) * pageSize) ?? []}
        isLoading={isLoading}
        subHeader={subHeader()}
        footer={
          <Grid item display={"flex"} justifyContent={"flex-end"}>
            <Pagination
              variant={"complex"}
              totalCount={adjustedGraphData?.length}
              page={page + 1}
              onPageChange={(page: number) => setPage(page - 1)}
              pageSize={pageSize}
              onPageSizeChange={(pageSize: number) => setPageSize(pageSize)}
              hidePageSize
            />
          </Grid>
        }
        graphHeight={300}
        indexBy={"reason"}
        keys={graphUniqueKeys}
        groupMode="stacked"
        emptyState={
          insight.status !== InsightList.status.SUCCEEDED
            ? {
                title: `${t("label.insight")} ${t(`status.${insight.status?.toLocaleLowerCase()}`, {
                  defaultValue: insight.status,
                })?.toLocaleLowerCase()}`,
              }
            : isEmpty
              ? { title: t("LabelReasonFeedback.noData") }
              : undefined
        }
      />
    </Grid>
  )
}
